import * as React from 'react';
import AppHistory from '__common/modules/history';
import { getProductName, isNxtHorizon, isSolarMount, products } from '__common/constants/products';
import { SET_PROJECT_OPTION, SET_PRODUCT_ID, SET_RAILS_PRODUCT_ID } from 'actions';
import { UPDATE_LITE_PROJECT_REQUEST } from 'v3/dashboard/liteProjectActions';
import { AttachmentType } from 'projectDesign/components/projectConfiguration/fields/attachmentType';
import { NxtHorizonRailType, SmFlushRailType, RoofType } from 'projectDesign/components/projectConfiguration/utils/constants';
import { userHasSmAscenderFlushPermission } from 'user/userGroups';

type Option = {
  label: string;
  value: number;
  railType: number;
};

type Props = {
  dispatch: Function;
  projectId?: string;
  productId?: number;
  railsProductId?: number;
  roofType?: number;
};

const SM_RAIL_OPTIONS: Option[] = [
  { label: 'SM Standard', value: products.solarmount_2, railType: SmFlushRailType.SM },
  { label: 'SM Light', value: products.solarmount_lt, railType: SmFlushRailType.SM_LT },
  ...(userHasSmAscenderFlushPermission()
    ? [
        {
          label: 'Ascender Rail',
          value: products.solarmount_ascender_flush,
          railType: SmFlushRailType.SM_ASCENDER_FLUSH,
        },
      ]
    : []),
];

const NXT_RAIL_OPTIONS: Option[] = [
  { label: 'NXT Standard', value: products.nxt_umount, railType: NxtHorizonRailType.NH },
  { label: 'NXT HD', value: products.nxt_horizon_hd, railType: NxtHorizonRailType.NH_HD },
];

function getOptions(productId?: number, roofType?: number): Option[] {
  if (!productId) return [];
  if (isSolarMount(productId)) {
    return roofType === RoofType.SHINGLE
      ? SM_RAIL_OPTIONS
      : SM_RAIL_OPTIONS.filter((option) => option.value !== products.solarmount_ascender_flush);
  }
  if (isNxtHorizon(productId)) return NXT_RAIL_OPTIONS;
  return [];
}

function getRailType(productId?: number, railsProductId?: number, roofType?: number): number | undefined {
  const options = getOptions(productId, roofType);
  const active = options.find((option) => option.value === railsProductId) || options[0];
  return active && active.railType;
}

function RailTypeSelector(props: Props) {
  const options = getOptions(props.productId, props.roofType);
  if (!options.length) return null;

  const activeValue = props.railsProductId && options.some((option) => option.value === props.railsProductId)
    ? props.railsProductId
    : options[0].value;

  const onChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextValue = Number(event.target.value);
    const nextOption = options.find((option) => option.value === nextValue);
    if (!nextOption || nextValue === props.railsProductId) return;
    const shouldDefaultAscenderAttachment =
      !!props.productId &&
      isSolarMount(props.productId) &&
      props.roofType === RoofType.SHINGLE &&
      nextValue === products.solarmount_ascender_flush;

    props.dispatch(SET_PRODUCT_ID(nextValue));
    props.dispatch(SET_PROJECT_OPTION('rail_type', nextOption.railType));
    props.dispatch(SET_RAILS_PRODUCT_ID(nextValue));
    if (shouldDefaultAscenderAttachment) {
      props.dispatch(SET_PROJECT_OPTION('attachment_type', AttachmentType.L_FOOT_ONLY));
    }
    if (props.projectId) {
      const onFullBomPage = typeof window !== 'undefined'
        && window.location
        && window.location.pathname
        && window.location.pathname.startsWith('/project/lite/bom/');
      if (onFullBomPage) {
        // The Full BOM page doesn't host the workspace, so drawingManager/panels
        // slices are absent → countLitePanels=0 → the autosave epic bails
        // unless we set allowEmptyPanels. We also pass configOverride so the
        // epic sends a partial update carrying only the changed fields (the
        // workspace variant would otherwise overwrite persisted fields from
        // store defaults). Both product_id and rail_type are included so the
        // backend persists the new rail family. Chain still ends in a forced
        // BOM regeneration + fetch.
        props.dispatch(UPDATE_LITE_PROJECT_REQUEST({
          projectId: props.projectId,
          regenBom: true,
          allowEmptyPanels: true,
          configOverride: {
            productId: nextValue,
            fields: {
              product_id: nextValue,
              rail_type: nextOption.railType,
              ...(shouldDefaultAscenderAttachment ? { attachment_type: AttachmentType.L_FOOT_ONLY } : {}),
            },
          },
        }));
      } else {
        props.dispatch(UPDATE_LITE_PROJECT_REQUEST({ projectId: props.projectId, regenBom: true }));
      }
    }
    if (props.projectId) {
      // Stay on the current route family so the user keeps the same view after
      // a rail-type swap. Both the workspace design page and the standalone
      // Full BOM page use SPA navigation via AppHistory.replace. The PUT
      // request must be allowed to complete — a hard reload (window.location
      // .replace) would abort the in-flight AJAX call before the new
      // product_id / rail_type reach the database. CompleteBomPage's
      // componentDidUpdate detects the URL slug change and dispatches
      // FETCH_LITE_BOM_REQUEST; the updateLiteProject epic also chains a
      // FETCH after the PUT succeeds. switchMap on fetchLiteBom ensures the
      // latest fetch (post-regen) wins.
      const onFullBomPage = typeof window !== 'undefined'
        && window.location
        && window.location.pathname
        && window.location.pathname.startsWith('/project/lite/bom/');
      const pathTail = `${getProductName(nextValue)}/${props.projectId}`;
      if (onFullBomPage) {
        AppHistory.replace(`/project/lite/bom/${pathTail}`);
      } else {
        AppHistory.replace(`/project/lite/design/${pathTail}`);
      }
    }
  };

  return (
    <div className="v3-bom-rail-type">
      <select
        className="v3-bom-finish-sel v3-bom-finish-sel--short"
        value={activeValue}
        onChange={onChange}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export { getOptions as getRailTypeOptions, getRailType };
export default RailTypeSelector;
